import React, { useState, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { colors } from '@iq/react-components';
import {
  Host,
  DataStore,
  SystemActivity,
  Close,
  RadioButtonSelected,
  Connect,
  Disconnect,
} from '@hitachivantara/uikit-react-icons';
import { HvOverflowTooltip } from '@hitachivantara/uikit-react-core';
import { DEVICE_STATUS_COLOR, STATUS_CLASS, STATUS_LABEL } from '../../../constants';

// Map known group labels to a NEXTicons 6.0 (Hitachi Vantara) icon component.
// `Host` uses two color slots ([background, glyph]); the rest use one.
//   - Edge   -> SystemActivity
//   - All connected computers / servers (MIMS, Main, SCM, MSM) -> Host
//   - NAS / Storage -> DataStore
const TYPE_ICON = {
  Edge: { Component: SystemActivity, twoTone: true },
  'MIMS Computer': { Component: Host, twoTone: true },
  MIMS: { Component: Host, twoTone: true },
  'MIMS Service': { Component: Host, twoTone: true },
  'Main Computer': { Component: Host, twoTone: true },
  Main: { Component: Host, twoTone: true },
  SCM: { Component: Host, twoTone: true },
  MSM: { Component: Host, twoTone: true },
  Storage: { Component: DataStore, twoTone: false },
  NAS: { Component: DataStore, twoTone: false },
};

// Short display label used in the card title (drops trailing "Computer"/"Service").
const SHORT_LABEL = {
  'MIMS Computer': 'MIMS',
  'MIMS Service': 'MIMS',
  'Main Computer': 'Main',
};

const iconForLabel = (label) => TYPE_ICON[label] || { Component: Host, twoTone: true };
const shortLabelFor = (label) => SHORT_LABEL[label] || label;

// Glyphs are drawn in dark gray on the white card per Figma.
const ICON_FILL = '#414141';

const CardIcon = ({ icon }) => {
  const { Component, twoTone } = icon;
  return (
    <span className="conn-card__icon">
      <Component
        iconSize="S"
        color={twoTone ? ['none', ICON_FILL] : [ICON_FILL]}
      />
    </span>
  );
};

// Plain (non-collapsible) card: icon, label, status text.
const StatusCard = ({ group }) => {
  const klass = STATUS_CLASS[group.status] || 'unknown';
  const icon = iconForLabel(group.label);
  return (
    <div className={classnames('conn-card', 'conn-card--plain', klass)}>
      <CardIcon icon={icon} />
      <span className="conn-card__label">{shortLabelFor(group.label)}</span>
      <span className="conn-card__status">{STATUS_LABEL[group.status] || 'Unknown'}</span>
    </div>
  );
};

// Chevron drawn as an SVG to match the Figma 6x12 stroked vector.
// Pointing right when collapsed; rotated 90deg (pointing down) when expanded.
const Chevron = ({ expanded }) => (
  <svg
    className={classnames('conn-card__chevron', { 'conn-card__chevron--down': expanded })}
    width="6"
    height="12"
    viewBox="0 0 6 12"
    aria-hidden="true"
  >
    <polyline
      points="0.75,0.75 5.25,6 0.75,11.25"
      fill="none"
      stroke={ICON_FILL}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Match the StateVisualization icon: a `he-status` glyph from
// @iq/react-components, tinted by status (green / red / grey).
const DeviceCell = ({ device }) => {
  const klass = STATUS_CLASS[device.status] || 'unknown';
  const color = DEVICE_STATUS_COLOR[device.status] || colors.Grey50;
  return (
    <div className={classnames('conn-device', klass)}>
      <RadioButtonSelected
        iconSize="XS"
        color={[color, '#f2f2f2']}
        className="icon"
      />
      <HvOverflowTooltip
        data={device.name}
        className="conn-device__name"
        placement="left"
        tooltipsProps={{
          PopperProps: { style: { zIndex: 2000 } },
          componentsProps: {
            tooltip: {
              style: {
                margin: 10,
                padding: 10,
                fontFamily: 'Open Sans, sans-serif',
              },
            },
          },
        }}
      />
    </div>
  );
};

// Devices are rendered in a flex-wrap row; the SCSS rule
// `.conn-device { min-width: calc(33.333% - gap) }` lets each row fit up to
// three short names, two medium names, or one very long name automatically.

// Helper to chunk an array into rows of n
function chunkArray(array, size) {
  const result = [];
  for (let index = 0; index < array.length; index += size) {
    result.push(array.slice(index, index + size));
  }
  return result;
}

const DeviceGrid = ({ devices }) => {
  const rows = chunkArray(devices, 3);
  return (
    <div className="conn-card__devices">
      {rows.map((row, index) => (
        <div
          key={index}
          className={classnames('conn-card__device-row', {
            'conn-card__device-row--border': index !== rows.length - 1,
          })}
        >
          {row.map((device) => (
            <DeviceCell
              key={device.objectId || device.name}
              device={device}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const CollapsibleCard = ({ group }) => {
  // Default expanded when the group is Disconnected (0) or Unknown (2);
  // collapsed only when fully Connected (1).
  const [expanded, setExpanded] = useState(group.status !== 1);
  const klass = STATUS_CLASS[group.status] || 'unknown';
  const icon = iconForLabel(group.label);
  const toggle = () => setExpanded((v) => !v);

  return (
    <div className={classnames('conn-card', klass, { 'conn-card--expanded': expanded })}>
      <button
        type="button"
        className="conn-card__header"
        onClick={toggle}
        aria-expanded={expanded}
      >
        <Chevron expanded={expanded} />
        <CardIcon icon={icon} />
        <span className="conn-card__label">
          {shortLabelFor(group.label)} ({group.devices.length})
        </span>
        <span className="conn-card__status">{STATUS_LABEL[group.status] || 'Unknown'}</span>
      </button>
      {expanded && <DeviceGrid devices={group.devices} />}
    </div>
  );
};

const SourceCards = ({ groups }) => (
  <div className="conn-source__cards">
    {groups.map((group) =>
      group.devices.length > 1 ? (
        <CollapsibleCard
          key={group.type}
          group={group}
        />
      ) : (
        <StatusCard
          key={group.type}
          group={group}
        />
      )
    )}
  </div>
);

const SourceSection = ({ source, hideTitle = false }) => (
  <div className="conn-source">
    {!hideTitle && <h5 className="conn-source__title">{source.sourceName}</h5>}
    <SourceCards groups={source.groups} />
  </div>
);

const CollapsibleSourceSection = ({ source }) => {
  const sourceStatus = source.status;
  // Default expanded when the source has a disconnected or unknown group.
  const [expanded, setExpanded] = useState(sourceStatus !== 1);
  const klass = STATUS_CLASS[sourceStatus] || 'unknown';
  const toggle = () => setExpanded((v) => !v);
  const titleRef = useRef(null);

  // Use Connect/Disconnect icon for source status
  const statusColor = DEVICE_STATUS_COLOR[sourceStatus] || colors.Grey50;
  const StatusIcon = sourceStatus === 1 ? Connect : Disconnect;

  const handleMouseEnter = useCallback(() => {
    const element = titleRef.current;
    if (element && element.scrollWidth > element.clientWidth) {
      element.title = source.sourceName;
    } else if (element) {
      element.removeAttribute('title');
    }
  }, [source.sourceName]);

  return (
    <div
      className={classnames('conn-source', 'conn-source--collapsible', klass, {
        'conn-source--expanded': expanded,
      })}
    >
      <button
        type="button"
        className="conn-source__header"
        onClick={toggle}
        aria-expanded={expanded}
      >
        <Chevron expanded={expanded} />
        <span
          className="conn-source__title"
          ref={titleRef}
          onMouseEnter={handleMouseEnter}
        >
          {source.sourceName}
        </span>
        <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center', marginRight: 2 }}>
          <StatusIcon
            iconSize="S"
            color={[statusColor]}
          />
        </span>
      </button>
      {expanded && <SourceCards groups={source.groups} />}
    </div>
  );
};

const ExtendedConnectivityStatus = ({ sources = [], onClose }) => {
  const singleSource = sources.length === 1;
  return (
    <div className="connectivity-container">
      <div className="connectivity-container__header">
        <h4>Station Connectivity</h4>
        {onClose && (
          <button
            type="button"
            className="connectivity-container__close"
            onClick={onClose}
            aria-label="Close"
          >
            <Close
              iconSize="XS"
              color={[colors.Grey75]}
            />
          </button>
        )}
      </div>

      <div className="connectivity-container__body">
        {sources.map((source) =>
          singleSource ? (
            <SourceSection
              key={source.sourceId}
              source={source}
              hideTitle
            />
          ) : (
            <CollapsibleSourceSection
              key={source.sourceId}
              source={source}
            />
          )
        )}
      </div>
    </div>
  );
};

export default ExtendedConnectivityStatus;
